var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import Dialog from 'rc-dialog';
import classNames from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
// import CloseOutlined from '@ant-design/icons/CloseOutlined';
import throttle from 'lodash/throttle';
import Popover from '../popover';
import { getConfirmLocale } from './locale';
import Button from '../button';
import { convertLegacyProps } from '../button/button';
import LocaleReceiver from '../locale-provider/LocaleReceiver';
import { ConfigContext } from '../config-provider';
import { canUseDocElement } from '../_util/styleChecker';
import { getTransitionName } from '../_util/motion';
import PopupClose from '../_util/bonreeIcons/PopupClose';
let mousePosition;
// ref: https://github.com/ant-design/ant-design/issues/15795
const getClickPosition = (e) => {
    mousePosition = {
        x: e.pageX,
        y: e.pageY,
    };
    // 100ms 内发生过点击事件，则从点击位置动画展示
    // 否则直接 zoom 展示
    // 这样可以兼容非点击方式展开
    setTimeout(() => {
        mousePosition = null;
    }, 100);
};
// 只有点击事件支持从鼠标位置动画展开
if (canUseDocElement()) {
    document.documentElement.addEventListener('click', getClickPosition, true);
}
const Modal = props => {
    const { getPopupContainer: getContextPopupContainer, getPrefixCls, direction, } = React.useContext(ConfigContext);
    const handleCancel = (e) => {
        const { onCancel } = props;
        onCancel === null || onCancel === void 0 ? void 0 : onCancel(e);
    };
    const handleOk = (e) => {
        const { onOk } = props;
        onOk === null || onOk === void 0 ? void 0 : onOk(e);
    };
    const renderFooter = (locale) => {
        const { okText, okType, cancelText, confirmLoading, okButtonPopoverContent } = props;
        const okButtonShowPopover = typeof okButtonPopoverContent === 'number' ||
            typeof okButtonPopoverContent === 'string' ||
            React.isValidElement(okButtonPopoverContent);
        return (<>
        {okButtonShowPopover ? (<Popover content={okButtonPopoverContent}>
            <Button {...convertLegacyProps(okType)} loading={confirmLoading} onClick={handleOk} {...props.okButtonProps}>
              {okText || locale.okText}
            </Button>
          </Popover>) : (<Button {...convertLegacyProps(okType)} loading={confirmLoading} onClick={handleOk} {...props.okButtonProps}>
            {okText || locale.okText}
          </Button>)}
        <Button onClick={handleCancel} {...props.cancelButtonProps}>
          {cancelText || locale.cancelText}
        </Button>
      </>);
    };
    const { prefixCls: customizePrefixCls, footer, visible, wrapClassName, centered = true, getContainer, closeIcon, focusTriggerAfterClose = true } = props, 
    // autoAdjustContent = true,
    restProps = __rest(props, ["prefixCls", "footer", "visible", "wrapClassName", "centered", "getContainer", "closeIcon", "focusTriggerAfterClose"]);
    const prefixCls = getPrefixCls('modal', customizePrefixCls);
    const rootPrefixCls = getPrefixCls();
    const observerList = React.useRef([]);
    const domListRef = React.useRef();
    // 通过content，从而控制body滚动条显隐、header和footer阴影显隐
    const triggerClassNameByContent = (target) => {
        if (target) {
            const contentDom = target === null || target === void 0 ? void 0 : target.querySelector(`.${prefixCls}-content`);
            const headerDom = target === null || target === void 0 ? void 0 : target.querySelector(`.${prefixCls}-header`);
            const footerDom = target === null || target === void 0 ? void 0 : target.querySelector(`.${prefixCls}-footer`);
            if (contentDom) {
                if (contentDom.scrollHeight >= Math.floor(document.body.clientHeight * 0.8)) {
                    contentDom.classList.add(`${prefixCls}-auto-adjust`);
                    footerDom === null || footerDom === void 0 ? void 0 : footerDom.classList.add('hasShadow');
                }
                else {
                    contentDom.classList.remove(`${prefixCls}-auto-adjust`);
                    headerDom === null || headerDom === void 0 ? void 0 : headerDom.classList.remove('hasShadow');
                    footerDom === null || footerDom === void 0 ? void 0 : footerDom.classList.remove('hasShadow');
                }
            }
        }
    };
    // 处理浏览器窗口大小改变
    const handleResize = throttle(() => {
        var _a;
        (_a = domListRef.current) === null || _a === void 0 ? void 0 : _a.forEach(target => {
            triggerClassNameByContent(target);
        });
    }, 200);
    // 监控弹窗内容变化
    const handleObserve = () => {
        var _a;
        (_a = domListRef.current) === null || _a === void 0 ? void 0 : _a.forEach(target => {
            const contentDom = target === null || target === void 0 ? void 0 : target.querySelector(`.${prefixCls}-content`);
            const resizeObserver = new ResizeObserver(entries => {
                entries.forEach(() => {
                    triggerClassNameByContent(target);
                });
            });
            resizeObserver.observe(contentDom);
            observerList.current.push(resizeObserver);
        });
    };
    // 控制header footer阴影
    const triggerShadow = throttle((event) => {
        const bodyDom = event.target;
        if (bodyDom) {
            const headerDom = bodyDom.previousElementSibling;
            const footerDom = bodyDom.nextElementSibling;
            if (bodyDom.scrollTop > 0) {
                headerDom === null || headerDom === void 0 ? void 0 : headerDom.classList.add('hasShadow');
            }
            else {
                headerDom === null || headerDom === void 0 ? void 0 : headerDom.classList.remove('hasShadow');
            }
            // dom的实际宽高会带小数，但scrollHeight、clientHeight、scrollTop会向下取整，导致计算误差，这里多加1px，消除误差
            if (bodyDom.scrollHeight > bodyDom.clientHeight + bodyDom.scrollTop + 1) {
                footerDom === null || footerDom === void 0 ? void 0 : footerDom.classList.add('hasShadow');
            }
            else {
                footerDom === null || footerDom === void 0 ? void 0 : footerDom.classList.remove('hasShadow');
            }
        }
    }, 200);
    // 处理弹窗body内容滚动
    const handleBodyScroll = () => {
        var _a;
        (_a = domListRef.current) === null || _a === void 0 ? void 0 : _a.forEach(target => {
            const bodyDom = target === null || target === void 0 ? void 0 : target.querySelector(`.${prefixCls}-body`);
            bodyDom === null || bodyDom === void 0 ? void 0 : bodyDom.addEventListener('scroll', triggerShadow);
        });
    };
    // 去除弹窗body内容滚动监听
    const removeBodyEventListener = () => {
        var _a;
        (_a = domListRef.current) === null || _a === void 0 ? void 0 : _a.forEach(target => {
            const bodyDom = target === null || target === void 0 ? void 0 : target.querySelector(`.${prefixCls}-body`);
            bodyDom === null || bodyDom === void 0 ? void 0 : bodyDom.removeEventListener('scroll', triggerShadow);
        });
    };
    React.useEffect(() => {
        requestAnimationFrame(() => {
            if (props.visible) {
                domListRef.current = document.querySelectorAll(`.${prefixCls}`);
                window.addEventListener('resize', handleResize);
                handleBodyScroll();
                handleObserve();
            }
        });
        return () => {
            window.removeEventListener('resize', handleResize);
            removeBodyEventListener();
            if (observerList.current.length) {
                observerList.current.forEach(item => {
                    item.disconnect();
                });
            }
        };
    }, [props.visible]);
    const defaultFooter = (<LocaleReceiver componentName="Modal" defaultLocale={getConfirmLocale()}>
      {renderFooter}
    </LocaleReceiver>);
    const closeIconToRender = (<span className={`${prefixCls}-close-x`}>{closeIcon || <PopupClose />}</span>);
    const wrapClassNameExtended = classNames(wrapClassName, {
        [`${prefixCls}-centered`]: !!centered,
        [`${prefixCls}-wrap-rtl`]: direction === 'rtl',
        [`${prefixCls}-no-header`]: !restProps.title,
        [`${prefixCls}-no-footer`]: footer === null,
    });
    return (<Dialog {...restProps} getContainer={getContainer === undefined ? getContextPopupContainer : getContainer} prefixCls={prefixCls} wrapClassName={wrapClassNameExtended} footer={footer === undefined ? defaultFooter : footer} visible={visible} mousePosition={mousePosition} onClose={handleCancel} closeIcon={closeIconToRender} focusTriggerAfterClose={focusTriggerAfterClose} transitionName={getTransitionName(rootPrefixCls, 'zoom', props.transitionName)} maskTransitionName={getTransitionName(rootPrefixCls, 'fade', props.maskTransitionName)}/>);
};
Modal.defaultProps = {
    width: 520,
    confirmLoading: false,
    visible: false,
    okType: 'primary',
};
export default Modal;
